<template>
  <v-container bg fill-height grid-list-md text-xs-center class="pt-0">
    <v-row v-if="loading" align="start">
      <v-col align="center">
        <v-progress-circular indeterminate color="primary" />
      </v-col>
    </v-row>

    <div v-else-if="!error" class="containerCards d-flex flex-column">
      <general-info
        v-if="props.procedure"
        :procedure="props.procedure"
        :name="props.procedure.goodName"
        :type="props.procedure.type"
        :types="types"
        :registry-number="props.procedure.registryNumber"
        :max-start-price="props.procedure.startMaxPrice"
        :isWithoutStartMaxPrice="props.procedure.isWithoutStartMaxPrice"
        :isClosed="isClosed"
        :isNds="props.procedure.isNds"
        @update:name="$emit('update:procedureGoodName', $event)"
        @update:type="$emit('update:procedureType', $event)"
      >
        <StatusInfo
          v-if="props.status"
          :name="props.status.name"
          :code="props.status.code"
          :result="props.result"
          :date="date ? $t(date) : date"
          :status-style="statusStyle"
        />
        <procedure-actions
          v-if="actionsInfo"
          class="position-relative"
          :item="actionsInfo"
          :tableModel="{ reload: load }"
        />
      </general-info>

      <Card
          v-if="isCanceled && showCancelDetails"
      >
        <v-card-title class="pa-0" primary-title>{{ $t('Причина отмены процедуры') }}</v-card-title>
        <div class="cancel-reason-message">
          {{ props.procedure.cancelReason?.message }}
        </div>
      </Card>

      <slot />
    </div>

    <v-row v-else align="start" justify="center" class="d-flex align-self-start">
      <v-col align="center">{{ $t('Процедура завершена') }}</v-col>
    </v-row>

    <ConfirmDialog ref="confirm" />

    <v-dialog persistent width="300" :model-value="!!loadingDialogText" :scrim="false">
      <v-card color="primary" dark>
        <v-card-text>
          {{ loadingDialogText ? $t(loadingDialogText) : loadingDialogText }}
          <v-progress-linear indeterminate color="white" class="mb-0" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import {computed, onMounted, ref, watch, watchEffect} from 'vue';
import { toast } from '@/lib/toast';
import { daysUntil, formatCases, formatDateWithOnlyTime, FORMAT_CASES_DAY, formatMinuteTime } from '../filters';
import ProcedureActions from "./ProcedureActions.vue";
import ConfirmDialog from "@intec/vue-components/form/ConfirmDialog.vue";
import GeneralInfo from '../components/procedure/GeneralInfo.vue';
import StatusInfo from "@/components/procedure/StatusInfo.vue";
import Card from "@intec/vue-components/form/Card.vue";
import { useRoute } from "vue-router";
import {useLibraryStore} from "@/stores/library";
import { useProcedureStepStore } from "@/stores/procedureStepStore";
import { useI18n } from 'vue-i18n';

const route = useRoute();
const libraryStore = useLibraryStore();

interface Procedure {
  applicationDeadline?: string
  reviewDeadline?: string
  summingStart?: string
  goodName?: string
  registryNumber?: string
  startMaxPrice?: string
  type?: string
  [key: string]: any
  isWithoutStartMaxPrice: boolean
  isNds?: boolean
  cancelReason?: {
    message: string
    date: Date
  }
}

interface Props {
  id?: string
  procedure: Procedure
  result?: object
  types: object[]
  actionsInfo?: object
  status: {
    name: string
    code: string
  }
  loadingDialogText?: string
  loadFunc: (procedureId?: number, isPublicRoute?: boolean) => Promise<void>
  isClosed: boolean,
  showCancelDetails: boolean,
}

const props = withDefaults(defineProps<Props>(), {
  loadFunc: async () => {},
  showCancelDetails: false
});

const emit = defineEmits<{
  'update:procedureGoodName': [value: string]
  'update:procedureType': [value: string]
  loadError: [error: any]
}>()

const { t } = useI18n();

const error = ref<boolean>(false);
const loading = ref<boolean>(false);
const confirm = ref(false)

const load = async () => {
  error.value = false;
  loading.value = true;
  try {
    await props.loadFunc(
        parseInt(props.id) || null,
        route.name === 'public.procedures.view'
    );
  } catch (e) {
    error.value = true;
    if (e.status !== '403') {
      toast.error(e.message);
      emit('loadError', e);
    }
  } finally {
    loading.value = false;
  }
}

const procedureStepStore = useProcedureStepStore();

const statusStyle = computed<string>(
    () => props.status && procedureStepStore.getStatusStyle(props.status.code)
);

const isCanceled = computed<boolean>(
    () => props.status && props.procedure.stepCode === 'canceled'
);

const date = computed<string>(() => {
  // Даем стороннему модулю шанс вернуть кастомную дату
  const override = libraryStore.callFunctionByCode('overrideDateText', {
    procedure: props.procedure,
    status: props.status,
  }).overrideDateText ?? null;

  if (typeof override === 'string') {
    return override;
  }

  if (isCanceled.value) {
    let canceledDate = props.procedure.cancelReason ? props.procedure.cancelReason.date : props.procedure.canceledAt;

    return t(`Дата и время отмены: {_1}`, {'_1': formatMinuteTime(canceledDate)});
  }

  const meta = procedureStepStore.getDeadlineMeta(props.status?.code);
  if (!meta) return null;

  const rawDate = props.procedure[meta.field];
  if (!rawDate) return null;

  const formatted = formatDateWithOnlyTime(rawDate);
  const daysLeft: number = daysUntil(rawDate);

  let result = `${meta.label} ${formatted}`;
  if (daysLeft > 0) {
    result += ` (${daysLeft} ${formatCases(daysLeft, FORMAT_CASES_DAY)})`;
  }

  return result;
});

watchEffect(() => {
  if (route.query.action) {
    libraryStore.callActionByCode(route.query.action, { procedure: {id: props.id, ...props.procedure} });
  }
})
onMounted(load);
watch(() => props.id, load);

defineExpose({
  confirm
})
</script>

<style scoped>
  .cancel-reason-message {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: rgba(var(--black-color), 0.8);
  }
</style>
